import type { AdminUserListItem, Role, UserStatus } from '@hemmafixa/shared';
import {
  Avatar,
  Badge,
  DataTable,
  EmptyState,
  MasterDetail,
  PageHeader,
  Pagination,
  SearchField,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  StatusBadge,
  UnderlineTabs,
  type ColumnDef,
} from '@hemmafixa/ui';
import { MousePointerClick, Users } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { fill, useI18n } from '@/i18n/context';
import { DetailPanel, FullPageLink, SelectableCard } from '../shared/master-detail-parts';
import { QueryView } from '../shared/query-view';
import { useStatuses } from '../shared/statuses';
import { useSelectedParam, useShownSelection } from '../shared/use-selected-param';
import { useAdminUsers } from './api';
import { AdminUserDetail } from './user-page';

const PAGE_SIZE = 20;

/** Every account, newest first (P2-10). */
export function UsersPage() {
  const { t, relative } = useI18n();
  const statuses = useStatuses();
  const [picked, setSelected] = useSelectedParam();
  const [search, setSearch] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [role, setRole] = useState<Role | 'all'>('all');
  const [status, setStatus] = useState<UserStatus | 'all'>('all');
  const [page, setPage] = useState(1);
  const u = t.admin.users;

  // Ask the API once typing pauses, not on every key
  useEffect(() => {
    const timer = setTimeout(() => setSearchQuery(search.trim()), 300);
    return () => clearTimeout(timer);
  }, [search]);

  const query = useAdminUsers({
    q: searchQuery || undefined,
    role: role === 'all' ? undefined : role,
    status: status === 'all' ? undefined : status,
    page,
    pageSize: PAGE_SIZE,
  });
  const selected = useShownSelection(
    picked,
    query.data?.items.map((item) => item.id),
  );

  const columns = useMemo<ColumnDef<AdminUserListItem, unknown>[]>(
    () => [
      {
        accessorKey: 'name',
        header: u.columns.name,
        cell: ({ row }) => (
          <span className="flex items-center gap-2.5">
            <Avatar seed={row.original.id} name={row.original.name} size="sm" />
            {row.original.name}
          </span>
        ),
      },
      { accessorKey: 'email', header: u.columns.email },
      {
        accessorKey: 'roles',
        header: u.columns.roles,
        enableSorting: false,
        cell: ({ row }) => (
          <div className="flex flex-wrap gap-1">
            {row.original.roles.map((r) => (
              <Badge key={r}>{t.areas[r]}</Badge>
            ))}
          </div>
        ),
      },
      {
        accessorKey: 'status',
        header: u.columns.status,
        cell: ({ row }) => <StatusBadge status={row.original.status} statuses={statuses.user} />,
      },
      {
        accessorKey: 'lastLoginAt',
        header: u.columns.lastLogin,
        cell: ({ row }) => (row.original.lastLoginAt ? relative(row.original.lastLoginAt) : u.neverLoggedIn),
      },
    ],
    [t, u, relative, statuses],
  );

  const filtered = search !== '' || role !== 'all' || status !== 'all';
  const resetPage =
    <T,>(set: (value: T) => void) =>
    (value: T) => {
      set(value);
      setPage(1);
    };

  return (
    <div className="grid gap-4">
      <PageHeader
        title={u.title}
        description={u.description}
        actions={
          <div className="flex w-full gap-3 sm:w-auto">
            <SearchField
              className="flex-1 sm:w-80 xl:w-96"
              value={search}
              onChange={resetPage(setSearch)}
              placeholder={u.searchPlaceholder}
            />
            <Select value={role} onValueChange={(value) => resetPage(setRole)(value as Role | 'all')}>
              <SelectTrigger tone="solid" aria-label={u.columns.roles}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{u.allRoles}</SelectItem>
                {(['client', 'provider', 'admin'] as const).map((r) => (
                  <SelectItem key={r} value={r}>
                    {t.areas[r]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        }
      />
      <div className="flex items-center justify-between gap-3">
        <UnderlineTabs
          label={u.columns.status}
          value={status}
          onChange={resetPage(setStatus)}
          options={[
            { value: 'all' as const, label: t.common.all },
            ...(['active', 'suspended'] as const).map((s) => ({ value: s, label: t.status.user[s] })),
          ]}
        />
        {/* Beside the tabs, so the details start level with the list */}
        {selected && (
          <FullPageLink to={`/admin/users/${selected}`} className="hidden shrink-0 lg:inline-flex" />
        )}
      </div>
      <MasterDetail
        divided
        open={selected !== null}
        onClose={() => setSelected(null)}
        labels={{ details: u.title }}
        placeholder={<EmptyState icon={MousePointerClick} title={t.common.pickItem} />}
        detail={
          selected ? (
            <DetailPanel fullPage={`/admin/users/${selected}`} linkBesideTabs>
              <AdminUserDetail id={selected} />
            </DetailPanel>
          ) : null
        }
        list={
          <>
            <QueryView query={query}>
              {(result) => (
                <>
                  <DataTable
                    layout="cards"
                    columns={columns}
                    data={result.items}
                    getRowId={(user) => user.id}
                    renderCard={(user) => (
                      <SelectableCard
                        to={`/admin/users/${user.id}`}
                        selected={user.id === selected}
                        onSelect={() => setSelected(user.id)}
                      >
                        <div className="flex items-center gap-3">
                          <Avatar seed={user.id} name={user.name} size="md" />
                          <div className="grid min-w-0 flex-1 gap-0.5">
                            <p className="truncate font-semibold text-secondary">{user.name}</p>
                            <p className="truncate text-sm text-muted-foreground">
                              {user.email} · {user.roles.map((r) => t.areas[r]).join(', ')}
                            </p>
                          </div>
                          <StatusBadge status={user.status} statuses={statuses.user} />
                        </div>
                      </SelectableCard>
                    )}
                    empty={
                      filtered ? (
                        <EmptyState
                          icon={Users}
                          title={t.common.noMatchesTitle}
                          description={t.common.noMatchesText}
                        />
                      ) : (
                        <EmptyState icon={Users} title={u.empty} />
                      )
                    }
                  />
                  <Pagination
                    page={result.page}
                    pageSize={result.pageSize}
                    total={result.total}
                    onPageChange={setPage}
                    labels={{
                      previous: t.common.previous,
                      next: t.common.next,
                      showing: (from, to, total) => fill(t.common.showingRange, { from, to, total }),
                    }}
                  />
                </>
              )}
            </QueryView>
          </>
        }
      />
    </div>
  );
}
